import React, { useState } from 'react';

import { Heading } from '@/components/typography/heading';
import { useLocalZonePreference } from '@/hooks/useLocalZonePreference';
import type { ModelLog } from '@/types/logs';
import { truncate } from '@/utils/stringUtils';
import { formatISODateTime } from '@/utils/timeFormatter';

import ModelLogDetailView from '../logDetail/modelLogDetailView';
import styles from './modelLogViewer.module.scss';

type Props = {
  agentId: string;
  requestId?: string;
  correlactionId?: string;
  data: ModelLog[];
  initialSelectedId?: string;
  onSelectionChange?: ( id: string ) => void;
};

function ModelLogViewer( props: Props ) {
  const [ selectedId, setSelectedId ] = useState<string | null>( props.initialSelectedId ?? null );
  const selectedLog = props.data?.find( l => l.id === selectedId ) ?? null;
  const [ useLocalTimeZone ] = useLocalZonePreference();

  // Icon components
  const HashIcon = () => (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <line x1="4" y1="9" x2="20" y2="9"></line>
      <line x1="4" y1="15" x2="20" y2="15"></line>
      <line x1="10" y1="3" x2="8" y2="21"></line>
      <line x1="16" y1="3" x2="14" y2="21"></line>
    </svg>
  );

  const ClockIcon = () => (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <circle cx="12" cy="12" r="10"></circle>
      <polyline points="12 6 12 12 16 14"></polyline>
    </svg>
  );

  const ServerIcon = () => (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <rect x="2" y="2" width="20" height="8" rx="2" ry="2"></rect>
      <rect x="2" y="14" width="20" height="8" rx="2" ry="2"></rect>
      <line x1="6" y1="6" x2="6.01" y2="6"></line>
      <line x1="6" y1="18" x2="6.01" y2="18"></line>
    </svg>
  );

  const UserIcon = () => (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
      <circle cx="12" cy="7" r="4"></circle>
    </svg>
  );

  const BrainIcon = () => (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M9.5 2A2.5 2.5 0 0 1 12 4.5v15a2.5 2.5 0 0 1-4.96.44 2.5 2.5 0 0 1-2.96-3.08 3 3 0 0 1-.34-5.58 2.5 2.5 0 0 1 1.32-4.24 2.5 2.5 0 0 1 1.98-3A2.5 2.5 0 0 1 9.5 2Z"></path>
      <path d="M14.5 2A2.5 2.5 0 0 0 12 4.5v15a2.5 2.5 0 0 0 4.96.44 2.5 2.5 0 0 0 2.96-3.08 3 3 0 0 0 .34-5.58 2.5 2.5 0 0 0-1.32-4.24 2.5 2.5 0 0 0-1.98-3A2.5 2.5 0 0 0 14.5 2Z"></path>
    </svg>
  );

  const PackageIcon = () => (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <line x1="16.5" y1="9.4" x2="7.5" y2="4.21"></line>
      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
      <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
      <line x1="12" y1="22.08" x2="12" y2="12"></line>
    </svg>
  );

  const formatDateTime = ( dateString: string ) => formatISODateTime( dateString, useLocalTimeZone );

  return (
    <div className={ styles.container }>
      {/* Left Pane - List View */}
      <div className={ styles.leftPane }>
        <div className={ styles.leftPaneHeader }>
          <Heading as="h2" size="md" className={ styles.leftPaneTitle }>LLM Logs</Heading>
          <p className={ styles.leftPaneSubtitle }>
            {props.data?.length || 0} logs
          </p>
        </div>

        <div>
          {props.data?.map( ( log, index ) => (
            <div
              key={ log.id || index }
              onClick={ () => { setSelectedId( log.id ); props.onSelectionChange?.( log.id ); } }
              className={
                styles.logItem +
                ( selectedId === log.id ? ` ${ styles.logItemSelected }` : '' )
              }
              onMouseEnter={ ( e ) => {
                if ( selectedId !== log.id ) {
                  e.currentTarget.style.backgroundColor = '#f3f4f6';
                }
              } }
              onMouseLeave={ ( e ) => {
                if ( selectedId !== log.id ) {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }
              } }
            >
              <div className={ styles.logId }>
                <HashIcon />
                <span>{truncate( log.id, 20 )}</span>
              </div>

              <div className={ styles.logDetail }>
                <ClockIcon />
                <span>{formatDateTime( log.start_time )}</span>
              </div>

              <div className={ styles.logDetail }>
                <ServerIcon />
                <span>{log.model || 'Unknown Model'}</span>
              </div>

              <div className={ styles.logDetail }>
                <UserIcon />
                <span>{truncate( log.correlation_id, 25 )}</span>
              </div>

              <div className={ styles.logDetail }>
                <BrainIcon />
                <span>{log.agent_name || 'primary'}</span>
              </div>

              <div className={ styles.logDetail }>
                <PackageIcon />
                <span>
                  {log.constellation_name} v{log.constellation_version}
                </span>
              </div>

              <div className={ styles.logBadgeContainer }>
                <span
                  className={
                    log.error_string ? styles.errorBadge : styles.successBadge
                  }
                >
                  {log.error_string ? 'Error' : 'Success'}
                </span>
                <span style={ { fontSize: '12px', color: '#6b7280' } }>
                  {log.completion_tokens} tokens
                </span>
                <span style={ { fontSize: '12px', color: '#6b7280' } }>
                  ${log.cost?.toFixed( 4 ) || '0.0000'}
                </span>
              </div>
            </div>
          ) )}
        </div>
      </div>

      {/* Right Pane - Detail View */}
      <div className={ styles.rightPane }>
        {selectedLog ? (
          <div className={ styles.rightPaneContent }>
            <ModelLogDetailView selectedLog={ selectedLog }></ModelLogDetailView>
          </div>
        ) : (
          <div className={ styles.emptyState }>
            <div className={ styles.emptyStateContent }>
              <div className={ styles.emptyStateIcon }>
                <svg
                  width="48"
                  height="48"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#9ca3af"
                  strokeWidth="2"
                >
                  <rect x="2" y="2" width="20" height="8" rx="2" ry="2"></rect>
                  <rect x="2" y="14" width="20" height="8" rx="2" ry="2"></rect>
                  <line x1="6" y1="6" x2="6.01" y2="6"></line>
                  <line x1="6" y1="18" x2="6.01" y2="18"></line>
                </svg>
              </div>
              <p>Select a log to view details</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
export default ModelLogViewer;
